import { Controller, useFormContext } from 'react-hook-form';
import { Datepicker, Input, ToggleValueInput } from 'shared-ui';
import { useEffect } from 'react';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { AddUserFormBody } from '../AddUser.types';
import { useAppSelectOptions } from 'hooks/useAppSelectOptions/useAppSelectOptions';
import { Form } from 'ui/form/Form';
import { getEndOfToday } from 'helpers/getEndOfToday';
import { UserStatusEnum } from 'api/users/users.enum';
import { DateFormat } from 'core/global.enum';

export const AddUserBasicDataForm = () => {
  const { formatMessage } = useLocale();

  const {
    control,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
  } = useFormContext<AddUserFormBody>();

  const { userStatusOptions } = useAppSelectOptions();
  const isUserInactive = watch('status') === UserStatusEnum.NIEAKTYWNY;

  useEffect(() => {
    setValue('dataDo', null);
    clearErrors('dataDo');
  }, [isUserInactive]);

  return (
    <>
      <Controller
        name="username"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            required
            label={formatMessage({ id: AppMessages['users.addUser.field.name.label'] })}
            placeholder={formatMessage({ id: AppMessages['users.addUser.field.name.placeholder'] })}
            errorMessage={errors?.username?.message}
            autoComplete="new-password"
          />
        )}
      />
      <Form.ControlledSelect
        control={control}
        name="status"
        required
        label={formatMessage({ id: AppMessages['users.addUser.field.status.label'] })}
        withHelperText
        errorMessage={errors?.status?.message}
        placeholder={formatMessage({ id: AppMessages['common.list.placeholder'] })}
        options={userStatusOptions}
      />
      <Controller
        name="dataDo"
        control={control}
        render={({ field }) => (
          <Datepicker
            {...field}
            disabled={isUserInactive}
            label={formatMessage({ id: AppMessages['users.addUser.field.date.label'] })}
            placeholderText={formatMessage({ id: AppMessages['users.addUser.field.date.placeholder'] })}
            minDate={new Date()}
            withCalendarIcon
            withDatepickerPopover
            isClickable
            errorMessage={errors?.dataDo?.message}
            popoverOrigin={{
              vertical: -80,
              horizontal: 60,
            }}
            customDateFormatOnOpen={getEndOfToday}
            withoutTimeInput
            mediumPlaceholderFontSize
            inputFormat={DateFormat.DATE}
          />
        )}
      />
      <div />
      <Controller
        name="password"
        control={control}
        render={({ field }) => (
          <ToggleValueInput
            {...field}
            required
            label={formatMessage({ id: AppMessages['users.addUser.field.password.label'] })}
            placeholder={formatMessage({ id: AppMessages['users.addUser.field.password.placeholder'] })}
            errorMessage={errors?.password?.message}
            type="password"
            autoComplete="new-password"
          />
        )}
      />
      <Controller
        name="passwordConfirmation"
        control={control}
        render={({ field }) => (
          <ToggleValueInput
            {...field}
            required
            label={formatMessage({ id: AppMessages['users.addUser.field.passwordConfirmation.label'] })}
            placeholder={formatMessage({ id: AppMessages['users.addUser.field.passwordConfirmation.placeholder'] })}
            errorMessage={errors?.passwordConfirmation?.message}
            type="password"
          />
        )}
      />
    </>
  );
};
